import { VerificationServiceConfiguration } from "@idw-types/data/verification-service-configuration";
import { IdentityProviderConfiguration } from "@idw-types/data/identity-providers";
import { FormattedAlertEventHistory } from "@idw-types/data/alert-event-history";
import { formatDateToString } from "./date-util";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { FormattedExportAssertionFraudResult } from "@idw-types/data/export-assertion-fraud-result";
import { FormattedVerificationTransaction } from "@idw-types/data/reports";
import {
    FormattedVerificationTransactionDetailsClaim,
    FormattedVerificationTransactionDetailsPolicyDecision,
    FormattedVerificationTransactionDetailsScore,
    FormattedVerificationTransactionDetailsTransactionEvent
} from "@idw-types/data/verification-transaction-details";
import { WorkflowConfigurationExport } from "@idw-types/data/workflow-configuration-export";

const toCSV = (table: string[][]) => {
    return table
        .map(row =>
            row
                .map(cell => {
                    // We remove blanks and check if the column contains
                    // other whitespace,`,` or `"`.
                    // In that case, we need to quote the column.
                    if (cell.replace(/ /g, '').match(/[\s,"]/)) {
                        return '"' + cell.replace(/"/g, '""') + '"';
                    }
                    return cell;
                })
        );
};

export const convertFileToBase64 = (file: File): Promise<string> => new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => {
        console.error("An error occurred while converting the file to base64:", error)
        resolve(null);
    }
});

export const downloadVerificationServiceConfigurationFile = (fileName: string, data: VerificationServiceConfiguration) => {
    try {
        const json = JSON.stringify(data);
        downloadJSONFile(fileName, json);
    } catch (error) {
        console.error(error);
    }
};

export const downloadWorkflowConfigurationFile = (fileName: string, data: WorkflowConfigurationExport) => {
    try {
        const json = JSON.stringify(data);
        downloadJSONFile(fileName, json);
    } catch (error) {
        console.error(error);
    }
};

export const downloadIdentityProviderConfigurationFile = (fileName: string, data: IdentityProviderConfiguration) => {
    try {
        const json = JSON.stringify(data);
        downloadJSONFile(fileName, json);
    } catch (error) {
        console.error(error);
    }
};

export const downloadAlertEventsFile = async (fileName: string, data: Array<FormattedAlertEventHistory>) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.alertEvents.columns.dateTriggered.header,
            resources.exports.alertEvents.columns.alert.header,
            resources.exports.alertEvents.columns.organization.header,
            resources.exports.alertEvents.columns.workflow.header,
            resources.exports.alertEvents.columns.service.header,
            resources.exports.alertEvents.columns.credential.header,
            resources.exports.alertEvents.columns.cause.header
        ]];
        data.forEach(alertEvent => {
            csvFormattedData.push([
                alertEvent.dateTriggered ? formatDateToString(alertEvent.dateTriggered, DISPLAY_DATE_WITH_TIME) : resources.common.notFound,
                alertEvent.alert?.name || resources.common.notFound,
                alertEvent.company.name || resources.common.notFound,
                alertEvent.cause?.workflow?.name || resources.common.notFound,
                alertEvent.cause?.rpService?.serviceName || resources.common.notFound,
                alertEvent.cause?.credentials?.join(",") || resources.common.notFound,
                `${alertEvent.cause?.assertion?.shortName} = ${alertEvent.rule?.assertionStatuses?.[0]}`
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadAssertionFraudResultsFile = async (
    fileName: string,
    data: Array<FormattedExportAssertionFraudResult>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.fraudReport.columns.date.header,
            resources.exports.fraudReport.columns.asi.header,
            resources.exports.fraudReport.columns.credential.header,
            resources.exports.fraudReport.columns.mbun.header,
            resources.exports.fraudReport.columns.assertion.header,
            resources.exports.fraudReport.columns.claimStatus.header,
            resources.exports.fraudReport.columns.riskLevel.header,
            resources.exports.fraudReport.columns.finalPolicyDecision.header,
            resources.exports.fraudReport.columns.relyingParty.header,
            resources.exports.fraudReport.columns.verificationService.header,
        ]];
        data.forEach(exportFraudReportResult => {
            csvFormattedData.push([
                exportFraudReportResult.date ? formatDateToString(exportFraudReportResult.date, DISPLAY_DATE_WITH_TIME) : "null",
                exportFraudReportResult.asi || "null",
                exportFraudReportResult.credential || "null",
                exportFraudReportResult.mbun || "null",
                exportFraudReportResult.assertion?.shortName || "null",
                exportFraudReportResult.claimStatus || "null",
                exportFraudReportResult.riskLevel?.label || "null",
                exportFraudReportResult.finalPolicyDecision || "null",
                exportFraudReportResult.relyingParty?.name || "null",
                exportFraudReportResult.rpAXNService?.serviceName || "null"
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadVerificationTransactionsFile = async (
    fileName: string,
    data: Array<FormattedVerificationTransaction>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.verificationTransactions.columns.transactionId.header,
            resources.exports.verificationTransactions.columns.date.header,
            resources.exports.verificationTransactions.columns.relyingParty.header,
            resources.exports.verificationTransactions.columns.verificationService.header,
            resources.exports.verificationTransactions.columns.credential.header,
            resources.exports.verificationTransactions.columns.score.header,
            resources.exports.verificationTransactions.columns.policyDecision.header,
            resources.exports.verificationTransactions.columns.appId.header
        ]];
        data.forEach(verificationTransaction => {
            csvFormattedData.push([
                verificationTransaction.transactionID,
                verificationTransaction.date ? formatDateToString(verificationTransaction.date, DISPLAY_DATE_WITH_TIME) : "",
                verificationTransaction.relyingParty?.name || "",
                verificationTransaction.serviceName || "",
                verificationTransaction.credential || "",
                verificationTransaction.score || "",
                verificationTransaction.policyDecision || "",
                verificationTransaction.appID || ""
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadVerificationTransactionDetailsPolicyDecisionsFile = async (
    fileName: string,
    data: Array<FormattedVerificationTransactionDetailsPolicyDecision>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.date.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.verificationService.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.apiKey.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.policy.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.decision.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.stepUpVerificationService.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.stepUpApiKey.header,
            resources.exports.verificationTransactionDetailsPolicyDecisions.columns.decisionDetails.header,
        ]];
        data.forEach(policyDecision => {
            csvFormattedData.push([
                policyDecision.date ? formatDateToString(policyDecision.date, DISPLAY_DATE_WITH_TIME) : "",
                policyDecision.rpServiceName || "",
                policyDecision.apiKey || "",
                policyDecision.policyName || "",
                policyDecision.decision || "",
                policyDecision.obligationRPServiceName || "",
                policyDecision.obligationAPIKey || "",
                "" //todo - fix decisionDetails
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadVerificationTransactionDetailsScoresFile = async (
    fileName: string,
    data: Array<FormattedVerificationTransactionDetailsScore>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.verificationTransactionDetailsScores.columns.date.header,
            resources.exports.verificationTransactionDetailsScores.columns.scoreType.header,
            resources.exports.verificationTransactionDetailsScores.columns.score.header,
        ]];
        data.forEach(score => {
            csvFormattedData.push([
                score.date ? formatDateToString(score.date, DISPLAY_DATE_WITH_TIME) : "",
                score.scoreType || "",
                `${score.score}`,
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadVerificationTransactionDetailsClaimsAssertionsFile = async (
    fileName: string,
    data: Array<FormattedVerificationTransactionDetailsClaim>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.date.header,
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.verificationService.header,
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.apiKey.header,
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.serviceOffering.header,
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.claim.header,
            resources.exports.verificationTransactionDetailsClaimsAssertions.columns.claimStatus.header
        ]];
        data.forEach(claim => {
            csvFormattedData.push([
                claim.effectiveDate ? formatDateToString(claim.effectiveDate, DISPLAY_DATE_WITH_TIME) : "",
                claim.rpServiceName || "",
                claim.apiKey || "",
                claim.serviceOfferingName || "",
                claim.claimType || "",
                claim.claimStatus || ""
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

export const downloadVerificationTransactionDetailsTransactionEventsFile = async (
    fileName: string,
    data: Array<FormattedVerificationTransactionDetailsTransactionEvent>
) => {
    try {
        const csvFormattedData: Array<Array<string>> = [[
            resources.exports.verificationTransactionDetailsTransactionEvents.columns.date.header,
            resources.exports.verificationTransactionDetailsTransactionEvents.columns.verificationService.header,
            resources.exports.verificationTransactionDetailsTransactionEvents.columns.apiKey.header,
            resources.exports.verificationTransactionDetailsTransactionEvents.columns.transactionEventDescription.header,
            resources.exports.verificationTransactionDetailsTransactionEvents.columns.details.header
        ]];
        data.forEach(transactionEvent => {
            const details = Object.keys(transactionEvent.details).map(key => `${key}: ${transactionEvent.details[key]}`).join(", ");
            csvFormattedData.push([
                transactionEvent.date ? formatDateToString(transactionEvent.date, DISPLAY_DATE_WITH_TIME) : "",
                transactionEvent.rpServiceName || "",
                transactionEvent.apiKey || "",
                transactionEvent.transactionType?.description || "",
                details,
            ]);
        });
        const sanitizedData = toCSV(csvFormattedData);
        await downloadCSVFile(fileName, sanitizedData);
    } catch (error) {
        console.error(error);
    }
};

const downloadJSONFile = (fileName: string, json: string) => {
    const blob = new Blob([json], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName + ".json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const downloadCSVFile = async (fileName: string, data: Array<Array<string>>) => {
    const csvContent = "data:text/csv;charset=utf-8," + data.map(line => line.join(",")).join("\n");
    const encodedURI = encodeURI(csvContent);
    const link = document.createElement('a');
    link.href = encodedURI;
    link.download = fileName + ".csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};